import { useCallback, useId, useImperativeHandle, useState } from "react";
import { Modal as RNModal, View } from "react-native";

import Button from "$src/core/components/atoms/Button/Button";
import Pressable from "$src/core/components/atoms/Pressable";
import ScrollView from "$src/core/components/atoms/ScrollView/ScrollView";
import Text from "$src/core/components/atoms/Text/Text";
import useTranslation from "$src/core/hooks/use-translation";

import $ from "./Modal.web.styles";
import type { ModalProps } from "./types";

const Modal = ({
  ref,
  title,
  children,
  scrollable = false,
  onPresent,
  onDismiss,
}: ModalProps) => {
  const key = useId();
  const { t } = useTranslation();

  const [visible, setVisible] = useState(false);

  const handlePresent = useCallback(() => {
    onPresent?.();
    setVisible(true);
  }, [onPresent]);

  const handleDismiss = useCallback(() => {
    onDismiss?.();
    setVisible(false);
  }, [onDismiss]);

  useImperativeHandle(
    ref,
    () => ({
      present: handlePresent,
      dismiss: handleDismiss,
    }),
    [handlePresent, handleDismiss],
  );

  return (
    <RNModal
      key={key}
      visible={visible}
      onRequestClose={handleDismiss}
      animationType="fade"
      transparent
    >
      <View style={$.overlay}>
        <Pressable
          style={$.backdrop}
          onPress={handleDismiss}
          accessibilityRole="none"
          accessibilityLabel={t("COMMON/CLOSE")}
          accessibilityHint={t("COMMON/CLOSE")}
        />
        <View style={$.modal}>
          <View style={$.header}>
            <Text preset="heading3" accessibilityRole="header" style={$.title}>
              {title}
            </Text>
            <Button
              name="close"
              preset="transparent"
              icon="close"
              onPress={handleDismiss}
            />
          </View>
          {scrollable ? (
            <ScrollView style={$.content} accessibilityRole="list">
              {children}
            </ScrollView>
          ) : (
            <View style={$.content}>{children}</View>
          )}
        </View>
      </View>
    </RNModal>
  );
};

export default Modal;
